import { useRouter } from 'next/router'

import { useAuth } from 'src/providers/AuthProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { InternalLink } from 'src/components/atoms/Link'
import { LogoRebrand } from 'src/components/atoms/LogoRebrand'
import { MenuDropdown } from 'src/components/molecules/MenuDropdown'
import { AccountDropdown } from 'src/components/organisms/AccountDropdown'

import {
  dashboardPath,
  donatePath,
  homePath,
  planterDashboardPath
} from 'src/config/paths'
import { ActiveProfileType, NavigationContext } from 'src/types/account'

import { LinkOption } from '../types'

export interface DesktopNavigationProps {
  links: LinkOption[]
  navigationContext: NavigationContext
}

export const DesktopNavigation = ({
  links,
  navigationContext
}: DesktopNavigationProps) => {
  const { user, memberships } = useUser()
  const { loginWithCustomRedirect } = useAuth()
  const router = useRouter()

  const renderLink = (menuItem: LinkOption) => {
    const { requiresUser, options, label, path } = menuItem

    if (!requiresUser || (requiresUser && user)) {
      if (options && options.length) {
        return (
          <MenuDropdown
            key={label}
            menuItem={menuItem}
            linkColorClass={
              navigationContext === NavigationContext.Public
                ? undefined
                : 'text-white'
            }
          />
        )
      }
      if (!path) return null
      return (
        <InternalLink
          color={
            navigationContext === NavigationContext.Public ? 'none' : 'white'
          }
          href={path as string}
          highlightActive
          className={`link inline-flex justify-center items-center h-11 px-6 transition-colors hover:text-white ${
            navigationContext === NavigationContext.Public
              ? 'text-shark'
              : 'hover:bg-forestDeep/60'
          } rounded-full hover:bg-forestDeep/60`}
          activeClassName={
            navigationContext === NavigationContext.Public
              ? 'text-white! bg-forestDeep font-semibold rounded-full'
              : 'bg-darkGreen text-kiwiLeafLight! rounded-full'
          }
        >
          <span
            className='font-medium uppercase truncate max-w-50'
            title={label}
          >
            {label}
          </span>
        </InternalLink>
      )
    }
  }

  const DEFAULT_PROFILE = 'assets/img/default-profile.png'

  const renderAccountLinks = () => {
    if (memberships && user) {
      return (
        <div key='accounts' className='mx-2 shrink-0'>
          <AccountDropdown
            accountName={user?.Account?.Name}
            avatarPath={
              user?.Account?.Avatar && user?.Account?.Avatar !== DEFAULT_PROFILE
                ? user.Account.Avatar
                : undefined
            }
          />
        </div>
      )
    }
  }

  return (
    <div className='w-full max-w-(--content-max-width) flex-row items-center hidden lg:relative lg:visible lg:flex lg:flex-[inherit]'>
      <InternalLink href={homePath} className='relative'>
        <LogoRebrand
          className={`link h-12.5 ${
            navigationContext === NavigationContext.Public
              ? 'text-forestDeep'
              : 'text-white'
          }`}
        />
      </InternalLink>
      <div className='flex items-center w-full ml-5'>
        {/* admin/user links */}
        <div className='flex flex-col sm:flex-row items-center w-full pl-0 list-none'>
          <div className='flex justify-end items-center w-full gap-4'>
            <div className='flex gap-1'>
              {links.map(linkOptions => renderLink(linkOptions))}
            </div>

            <div className='flex justify-between items-center'>
              {/* login link */}
              {!user && (
                <div className='flex items-center gap-2 mr-1'>
                  <ButtonRebrand
                    variant='forest'
                    onClick={() => {
                      loginWithCustomRedirect(dashboardPath())
                    }}
                  >
                    Log in
                  </ButtonRebrand>
                  <ButtonRebrand
                    variant='lavender'
                    onClick={() => {
                      router.push(donatePath())
                    }}
                  >
                    Donate
                  </ButtonRebrand>
                </div>
              )}
            </div>
            {user && navigationContext === NavigationContext.Public ? (
              <div className='flex justify-between items-center'>
                <ButtonRebrand
                  variant='forest'
                  onClick={() => {
                    router.push(
                      user?.ActiveProfileType === ActiveProfileType.Planter
                        ? planterDashboardPath()
                        : dashboardPath()
                    )
                  }}
                >
                  Account
                </ButtonRebrand>
              </div>
            ) : null}
            {/* account switch/auth links */}
            {user && navigationContext !== NavigationContext.Public
              ? renderAccountLinks()
              : null}
          </div>
        </div>
      </div>
    </div>
  )
}
